import { rgb2int } from '../../../libs/videoData';
import { TemplateColorProperties } from '../../template/types';

import type { CommonAttributeSettings } from '../common';
import { DEFAULT_ATTRIBUTE_SETTINGS, defineMetadata, createAttributeSetter } from '../common';

import {
    createTemplateAttributeGetter,
    createThemeRef,
    isTemplateReference,
    ThemeRefTypes,
    validateTemplateReference,
} from './common';

export const COLOR_ATTRIBUTE_TYPE = 'colorAttributeType';

function isValidColorValue(value: unknown): value is number {
    if (typeof value !== 'number') {
        return false;
    }
    if (value < 0x000000) {
        return false;
    }
    if (value > 0xffffff) {
        return false;
    }
    return true;
}

function checkAndFixColorValue(value: string | number | number[]): string | number {
    // convert hex to number
    if (typeof value === 'string' && value[0] === '#') {
        return parseInt(value.substr(1), 16);
    }
    // convert [R, G, B] to number
    if (Array.isArray(value)) {
        return rgb2int(value);
    }
    return value;
}

function colorValueValidator(
    this: { template?: Record<string, unknown[]>; name?: string },
    value: string | number | number[],
): void {
    const checkedValue = checkAndFixColorValue(value);
    if (typeof checkedValue === 'string' && isTemplateReference(checkedValue)) {
        if (!checkedValue.includes(ThemeRefTypes.COLORS)) {
            throw Error(`Invalid value for color theme ref: ${checkedValue}`);
        }
        validateTemplateReference.call(this, checkedValue, ThemeRefTypes.COLORS);
    } else if (!isValidColorValue(checkedValue)) {
        throw Error(`Invalid color value: ${checkedValue}`);
    }
}

export function templateColorByIndex(index: number): string {
    return createThemeRef(ThemeRefTypes.COLORS, index);
}

/**
 * Creates an attribute decorator used to describe a color property. This will enforce either a numeric
 * value between 0x0 and 0xFFFFFF or a theme reference string such as themeRef_colors_0.
 */
export function ColorAttribute(settings: Partial<CommonAttributeSettings> = {}) {
    settings = { ...DEFAULT_ATTRIBUTE_SETTINGS, ...settings };
    const validator = colorValueValidator;
    return function <This>(
        { get, set }: ClassAccessorDecoratorTarget<This, number>,
        context: ClassAccessorDecoratorContext,
    ): ClassAccessorDecoratorResult<This, number> {
        defineMetadata(settings, COLOR_ATTRIBUTE_TYPE, context, get);
        return {
            get: createTemplateAttributeGetter(
                get,
                ThemeRefTypes.COLORS,
                TemplateColorProperties.COLOR,
            ) as (this: This) => number,
            set: createAttributeSetter(validator, get, set),
        };
    };
}
